import React from 'react';
import PropTypes from 'prop-types';
import { Badge } from './Badge';

/**
 * QuotaBadge Component
 *
 * Displays quota usage with visual indicators and optional reset date.
 * Used in Create Stories page header to show remaining story quota.
 *
 * Features:
 * - Color-coded based on usage percentage (green → yellow → red)
 * - Shows used/limit or "Unlimited" for unlimited plans
 * - Optional tooltip with reset date
 * - Clickable to show quota details modal
 *
 * @example
 * <QuotaBadge
 *   quotaType="stories"
 *   used={45}
 *   limit={100}
 *   unlimited={false}
 *   resetsAt="2025-11-01T00:00:00Z"
 *   onClick={() => showQuotaModal()}
 * />
 */
export function QuotaBadge({
  quotaType = 'stories',
  used = 0,
  limit = 0,
  unlimited = false,
  resetsAt = null,
  onClick = null,
  className = '',
}) {
  // Calculate usage percentage for color coding
  const usagePercent = unlimited ? 0 : (used / limit) * 100;

  // Determine badge variant based on usage
  const getVariant = () => {
    if (unlimited) return 'purple';
    if (usagePercent >= 90) return 'red';
    if (usagePercent >= 70) return 'yellow';
    return 'green';
  };

  // Format reset date
  const formatResetDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'tomorrow';
      if (diffDays < 7) return `in ${diffDays} days`;

      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
      return null;
    }
  };

  // Format quota label
  const getQuotaLabel = () => {
    if (unlimited) {
      return 'Unlimited';
    }
    const remaining = limit - used;
    return `${remaining}/${limit} ${quotaType}`;
  };

  // Icon for quota badge
  const QuotaIcon = () => (
    <svg
      className="w-3 h-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );

  const resetLabel = formatResetDate(resetsAt);

  return (
    <div
      className={`inline-flex items-center gap-2 ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
      title={
        unlimited
          ? 'Unlimited quota'
          : resetLabel
          ? `Resets ${resetLabel}`
          : `${used} of ${limit} ${quotaType} used`
      }
    >
      <Badge variant={getVariant()} icon={<QuotaIcon />}>
        {getQuotaLabel()}
      </Badge>

      {/* Optional reset date indicator */}
      {!unlimited && resetLabel && (
        <span className="text-xs text-gray-500">
          Resets {resetLabel}
        </span>
      )}
    </div>
  );
}

QuotaBadge.propTypes = {
  quotaType: PropTypes.oneOf(['stories', 'regenerations', 'storage']),
  used: PropTypes.number,
  limit: PropTypes.number,
  unlimited: PropTypes.bool,
  resetsAt: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
};
